import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Upload, Star, CheckCircle, Clock } from 'lucide-react';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';

export function OnboardingCard() {
  const navigate = useNavigate();
  const onboardingStatus = useOnboardingStatus();

  if (onboardingStatus.loading) {
    return (
      <Card className="card-elevated">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-2 bg-muted rounded"></div>
            <div className="h-8 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't show anything if onboarding is complete
  const isCompleted = onboardingStatus.nextStep === 'done';
  if (isCompleted) {
    return null;
  }

  const steps = [
    {
      id: 'profile',
      title: 'Complete Profile',
      description: 'Set up your username and avatar',
      benefit: 'Join the community',
      icon: BookOpen,
      completed: onboardingStatus.hasProfile,
      path: '/onboarding/profile'
    },
    {
      id: 'genres',
      title: 'Select Genres',
      description: 'Pick favorites and rate books for personalized recommendations',
      benefit: 'Find your next read',
      icon: Star,
      completed: onboardingStatus.genresComplete,
      path: '/onboarding/genres'
    }
  ];

  const completedSteps = steps.filter(step => step.completed).length;

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Complete Your Setup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Complete Your Setup</h3>
            <Badge variant="secondary">
              {completedSteps}/{steps.length} Complete
            </Badge>
          </div>
          
          <Progress value={(completedSteps / steps.length) * 100} className="h-2" />
          
          <div className="space-y-3">
            {steps.map((step) => {
              const StepIcon = step.icon;
              return (
                <div key={step.id} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <StepIcon className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{step.title}</div>
                      <div className="text-sm text-muted-foreground">{step.description}</div>
                    </div>
                  </div>
                  {step.completed ? (
                    <CheckCircle className="w-5 h-5 text-success" />
                  ) : (
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              );
            })}
          </div>
          
          <Button 
            onClick={() => {
              if (onboardingStatus.nextStep === 'profile') {
                navigate('/onboarding/profile');
              } else if (onboardingStatus.nextStep === 'import') {
                navigate('/onboarding/import');
              } else if (onboardingStatus.nextStep === 'genres') {
                navigate('/onboarding/genres');
              } else {
                navigate('/onboarding');
              }
            }}
            className="w-full"
          >
            {completedSteps === 0 ? 'Get Started' : 'Continue Setup'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}